import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SiteContactsPanel } from '../features/contacts/SiteContactsPanel';
import { SiteContactSummary } from '../features/site-contact/SiteContactSummary';
import { SiteInventoryTab } from '../features/site-detail/SiteInventoryTab';
import { SitePickListsTab } from '../features/site-detail/SitePickListsTab';
import { SiteShipmentsTab } from '../features/site-detail/SiteShipmentsTab';
import { SiteWorkTab } from '../features/site-detail/SiteWorkTab';
import { SiteStatusLogTab } from '../features/site-status/SiteStatusLogTab';
import { SiteStatusPanel } from '../features/site-status/SiteStatusPanel';
import { useSiteFieldWork, useSiteOrders } from '../features/site-detail/site-data';
import { siteLabel } from '../lib/site-label';

const tabs = ['work', 'status', 'pick-lists', 'shipments', 'inventory'] as const;
type Tab = (typeof tabs)[number];
const tabLabels: Record<Tab, string> = {
  work: 'Field work',
  status: 'Status log',
  'pick-lists': 'Pick lists',
  shipments: 'Shipments',
  inventory: 'Inventory',
};

function isTab(value: string | null): value is Tab {
  return tabs.some((tab) => tab === value);
}

/**
 * Everything happening at one Site (tidaltech/koi#8208): its open field
 * work first, so Field Services can see what else to do on the same trip,
 * then its pick lists, shipments and devices. Each tab is its own
 * component in features/site-detail.
 */
export function SiteDetailPage() {
  const { customerId = '', siteId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const activeTab: Tab = isTab(tabParam) ? tabParam : 'work';
  const showClosed = params.get('closed') === 'true';

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  }

  const sites = useQuery({
    queryKey: ['sites', customerId],
    queryFn: () => inventoryApi.listSites(customerId),
    enabled: Boolean(customerId),
  });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const customer = customers.data?.find((candidate) => candidate.id === customerId);
  const site = (sites.data ?? []).find((candidate) => candidate.id === siteId);
  // For the tab counts; the tabs share these queries.
  const fieldWork = useSiteFieldWork(siteId);
  const orders = useSiteOrders(siteId);

  if (sites.isLoading) return <PageLoadingState label="Loading site…" />;
  if (sites.isError || !site) {
    return (
      <ErrorState
        title="Site could not be loaded"
        rpcName="ListSites"
        onRetry={() => void sites.refetch()}
      />
    );
  }

  const counts: Record<Tab, number | null> = {
    work: fieldWork.query.isSuccess ? fieldWork.open.length : null,
    status: null,
    'pick-lists': orders.query.isSuccess ? orders.open.length : null,
    shipments: null,
    inventory: null,
  };

  function renderActiveTab() {
    switch (activeTab) {
      case 'work':
        return (
          <SiteWorkTab
            siteId={siteId}
            showClosed={showClosed}
            onShowClosedChange={(checked) => setParam('closed', checked ? 'true' : '')}
          />
        );
      case 'status':
        return <SiteStatusLogTab customerId={customerId} siteId={siteId} />;
      case 'pick-lists':
        return <SitePickListsTab siteId={siteId} />;
      case 'shipments':
        return <SiteShipmentsTab siteId={siteId} />;
      case 'inventory':
        return <SiteInventoryTab siteId={siteId} />;
    }
  }

  return (
    <>
      <PageHeader
        backLink={
          <Link to={`/customers/${encodeURIComponent(customerId)}?tab=sites`}>Customer</Link>
        }
        title={siteLabel(site)}
        description={[
          'Site',
          site.region,
          site.labels?.country,
          site.officialSiteId ? `Official ID ${site.officialSiteId}` : undefined,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <Link to={`/field-work?q=${encodeURIComponent(site.name)}`} className="button-link">
            Find in Field work
          </Link>
        }
      />

      <SiteContactSummary site={site} />
      <SiteContactsPanel
        site={site}
        customerSites={sites.data ?? []}
        customerName={customer?.displayName ?? customer?.name ?? 'customer'}
      />
      <SiteStatusPanel site={site} onOpenLog={() => setParam('tab', 'status')} />

      <div className="detail-tabs" role="tablist" aria-label="Site sections">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={activeTab === tab ? 'active' : undefined}
            onClick={() => setParam('tab', tab === 'work' ? '' : tab)}
          >
            {tabLabels[tab]}
            {counts[tab] !== null ? <span className="tab-count">{counts[tab]}</span> : null}
          </button>
        ))}
      </div>

      {renderActiveTab()}
    </>
  );
}
